/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
// First, let's declare the module for jspdf-autotable

import { IGrammarExercise, IScoreBar, IUserAnswers } from "@/types";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const ExerciseTypes = {
  MULTIPLE_CHOICE: 'multiple_choice',
  TRUE_FALSE: 'true_false',
  MATCHING_OPTION: 'matching_option',
  GAP_FILL: 'gap_fill',
  GROUPINGS: 'groupings',
} as const;

type ExerciseType = typeof ExerciseTypes[keyof typeof ExerciseTypes];

// Add this helper function to strip HTML tags and decode entities
const stripHtmlAndFormatText = (html: string): string => {
  // First, create a temporary div to parse HTML
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Get text content (this removes all HTML tags)
  let text = temp.textContent || temp.innerText;

  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Remove extra whitespace
  text = text.replace(/\s+/g, " ").trim();

  return text;
};

export const generatePDF = (
  exercise: IGrammarExercise,
  exerciseResults: IScoreBar[],
  userAnswers: IUserAnswers,
) => {
  const doc = new jsPDF() as any;

  // Set initial document properties
  doc.setFontSize(12);

  // Add exercise type and name
  doc.setFont("helvetica", "normal");
  doc.text(
    exercise?.type
      ? exercise?.type?.charAt(0)?.toUpperCase() +
          exercise?.type?.slice(1)?.toLowerCase()
      : "Comprehension",
    20,
    20,
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(exercise?.name || "Exercise Results", 20, 30);

  // Draw a line
  doc.setLineWidth(0.5);
  doc.line(20, 35, 190, 35);

  let yPosition = 45;

  // Exercise description - with HTML tags stripped
  if (exercise?.text) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    // Split text into lines that fit the page width
    const formattedText = stripHtmlAndFormatText(exercise.text);
    const textLines = doc.splitTextToSize(formattedText, 170); // 170 is the max width

    // Add each line to the PDF
    textLines.forEach((line: string) => {
      doc.text(line, 20, yPosition);
      yPosition += 7; // Increment position for next line
    });

    yPosition += 10; // Add extra space after text block
  }

  exerciseResults.forEach(({ type, score, total }) => {
    let hasData = false;
    let tableData: any[] = [];

    switch (type as ExerciseType) {
      case ExerciseTypes.MULTIPLE_CHOICE:
        if (exercise.multiple_choice?.length > 0) {
          hasData = true;
          tableData = exercise.multiple_choice.map((q) => [
            q.question,
            q.options.find((o: any) => o.is_correct === 1)?.option || "",
            Number(userAnswers?.[`${q.id}|multiple_choice`]?.text_answer)
              ? "OK"
              : "Failed",
          ]);
        }
        break;

      case ExerciseTypes.TRUE_FALSE:
        if (exercise.true_false?.length > 0) {
          hasData = true;

          tableData = exercise.true_false.map((q) => [
            q.question,
            q.options.find((o) => o.is_correct === 1)?.option || "",
            Number(userAnswers?.[`${q.id}|true_false`]?.text_answer)
              ? "OK"
              : "Failed",
            q.explanation || "-",
          ]);
        }
        break;

      case ExerciseTypes.MATCHING_OPTION:
        if (exercise.matching_option?.length > 0) {
          hasData = true;
          tableData = exercise.matching_option.map((q) => [
            q.statement,
            q.correct_answer,
            Number(userAnswers?.[`${q.id}|matching_option`]?.text_answer)
              ? "OK"
              : "Failed",
          ]);
        }
        break;

      case ExerciseTypes.GAP_FILL:
        if (exercise.gap_fill?.length > 0) {
          hasData = true;

          tableData = exercise.gap_fill.map((q) => {
            const userAnswer = userAnswers?.[`${q.id}|gap_fill`]?.text_answer
              ?.trim()
              ?.toLowerCase()
              ?.split(":")
              ?.map((ua) => ua.trim());

            let statementParts = q.statement;

            q.correct_answer.split(":").forEach((correctAnswers) => {
              const splitCorrectAnswer = correctAnswers.split("|");

              const find =
                splitCorrectAnswer.find((correctAnswer) => {
                  correctAnswer = correctAnswer.trim().toLowerCase();

                  return userAnswer.some(
                    (userA) =>
                      correctAnswer.charAt(0) == userA.charAt(0) &&
                      correctAnswer.includes(userA),
                  );
                }) ?? splitCorrectAnswer[0].trim();

              statementParts = statementParts.replace(
                correctAnswers,
                `(${find})`,
              );
            });

            return [
              statementParts,
              "(" +
                q.correct_answer
                  .split(":")
                  .map((correctAnswers) => {
                    const splitCorrectAnswer = correctAnswers.split("|");

                    return (
                      splitCorrectAnswer?.find((correctAnswer) => {
                        correctAnswer = correctAnswer.trim().toLowerCase();

                        return userAnswer.some(
                          (userA) =>
                            correctAnswer.charAt(0) == userA.charAt(0) &&
                            correctAnswer.includes(userA),
                        );
                      }) ?? splitCorrectAnswer[0].trim()
                    );
                  })
                  .join("), (") +
                ")",
              "(" +
                (userAnswer.join("), (") ??
                  q.correct_answer
                    .split(":")
                    .map((correctAnswers) => {
                      const splitCorrectAnswers = correctAnswers.split("|");

                      return (
                        splitCorrectAnswers.find((correctAnswer) => {
                          correctAnswer = correctAnswer.trim().toLowerCase();

                          return userAnswer.some(
                            (userA) =>
                              correctAnswer.charAt(0) == userA.charAt(0) &&
                              correctAnswer.includes(userA),
                          );
                        }) ?? splitCorrectAnswers[0].trim()
                      );
                    })
                    .join("), (") ??
                  "Not answered") +
                ")",
            ];
          });
        }
        break;

      case ExerciseTypes.GROUPINGS:
        if (exercise.groupings?.length > 0) {
          hasData = true;
          tableData = exercise.groupings.map((g) => [
            g.category,
            g.word,
            userAnswers?.[`${g.id}|groupings`]?.text_answer || "Not answered",
          ]);
        }
        break;
    }

    if (hasData && tableData.length > 0) {
      // Exercise type header
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(
        type?.charAt(0)?.toUpperCase() +
          type?.slice(1)?.toLowerCase().split("_").join(" "),
        20,
        yPosition,
      );
      yPosition += 10;

      // Score
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Score: ${score}/${total} (${Math.round((score / total) * 100)}%)`,
        20,
        yPosition,
      );
      yPosition += 10;

      // Table
      doc.autoTable({
        startY: yPosition,
        head: [
          [
            "Question/Statement",
            "Correct Answer",
            "Your Answer",
            "Explanation",
          ].slice(0, tableData[0].length),
        ],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [128, 101, 201] },
        margin: { left: 20, right: 20 },
      });

      // Update Y position after table
      yPosition = doc.lastAutoTable.finalY + 10;
    }
  });

  return doc;
};

// For backward compatibility
export const handleDownload = async (
  exercise: IGrammarExercise,
  exerciseResults: IScoreBar[],
  user_answer?: IUserAnswers,
) => {
  const doc = generatePDF(exercise, exerciseResults, user_answer ?? {});
  doc.save(`${exercise?.name || "Exercise"}_Results.pdf`);
};